import { Button } from '@lightspeed/design-system-react'
import React, { useState } from 'react'
import { useT } from '@transifex/react'
import { useDispatch } from 'react-redux'
import { setSaleFulfilmentDetail } from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../popover/hooks/useFulfillmentHelper'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../models/fulfilment'
import { useSaleManager } from '../../../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../../constants'
import {
  HEAP_DATA_TRACK_UPDATE_BILLING_ADDRESS,
  HEAP_DATA_TRACK_USE_BILLING_ADDRESS_FOR_SHIPPING,
} from '../../../sale-model.constant'
import { AddOrEditAddressButton, EditAddressButtons } from './EditAddressButton'
import { useAddressModalHandlers } from './hooks/useAddressModalHandlers'

interface FormButtonOnEmptyShippingAddressProps {
  loading?: boolean
  fulfilmentDetail: FulfilmentDetail
  handleEmptyShippingCorrectBillingAddress?: () => void
  onClose?: (hasMarked?: any) => void
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

export const FormButtonOnEmptyShippingAddress: React.FC<FormButtonOnEmptyShippingAddressProps> = ({
  loading,
  fulfilmentDetail,
  handleEmptyShippingCorrectBillingAddress,
  onClose,
  onSave,
}) => {
  const t = useT()
  const { validateFulfillmentAddresses } = useFulfillmentHelper()
  const { error, status } = validateFulfillmentAddresses(fulfilmentDetail)
  const dispatch = useDispatch()
  const saleManager = useSaleManager()
  const [isClickedToOpenModal, setIsClickedToOpenModal] = useState(false)
  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const isIncorrectBillingAndNotEmptyShippingAddress =
    status === AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING ||
    status === AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING ||
    status === AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING

  const buttonType = isIncorrectBillingAndNotEmptyShippingAddress
    ? EditAddressButtons.edit
    : EditAddressButtons.add

  const { openAddressModal } = useAddressModalHandlers({
    type: buttonType,
    onClose,
    onSave,
  })

  const { openAddressModal: openEditAddressModal } = useAddressModalHandlers({
    type: 'edit',
    onClose,
    onSave,
  })

  const copyBillingAddressToShippingAddress = () => {
    const newFulfillmentDetail = {
      type: FulfilmentType.DELIVERY,
      note: fulfilmentDetail?.note || '',
      billingAddress: fulfilmentDetail?.billingAddress,
      shippingAddress:
        fulfilmentDetail?.billingAddress || fulfilmentDetail?.shippingAddress,
    }

    dispatch(setSaleFulfilmentDetail(newFulfillmentDetail))

    if (isCaptureAddressSellScreenEnabled)
      saleManager.updateFulfillmentDetails([newFulfillmentDetail])
  }

  return (
    <>
      <AddOrEditAddressButton
        variant={
          isIncorrectBillingAndNotEmptyShippingAddress ? 'go' : 'supplementary'
        }
        type={buttonType}
        hasError={Boolean(error)}
        text={
          isIncorrectBillingAndNotEmptyShippingAddress
            ? t('Update address', {
                _context: 'Popover button',
              })
            : t('Add new address', {
                _context: 'Modal button',
              })
        }
        onClose={onClose}
        onSave={onSave}
        onOpenModal={openAddressModal}
      />
      {status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING && (
        <Button
          onClick={() => {
            copyBillingAddressToShippingAddress()
            handleEmptyShippingCorrectBillingAddress &&
              handleEmptyShippingCorrectBillingAddress()
            onClose?.()
          }}
          variant="go"
          loading={loading}
          data-track={HEAP_DATA_TRACK_USE_BILLING_ADDRESS_FOR_SHIPPING}
        >
          {t('Use billing address for shipping', {
            _context: 'Modal button',
          })}
        </Button>
      )}
      {status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING &&
        (!isClickedToOpenModal ? (
          <Button
            onClick={() => {
              setIsClickedToOpenModal(true)
            }}
            variant="go"
            loading={loading}
            data-track={HEAP_DATA_TRACK_UPDATE_BILLING_ADDRESS}
          >
            {t('Update billing address', {
              _context: 'Modal button',
            })}
          </Button>
        ) : (
          <AddOrEditAddressButton
            type="edit"
            hasError={Boolean(error)}
            onClick={() => {
              copyBillingAddressToShippingAddress()
            }}
            onClose={onClose}
            variant="go"
            text={t('Update billing address', {
              _context: 'Modal button',
            })}
            isModalOpeningOnError={true}
            onOpenModal={openEditAddressModal}
            onSave={onSave}
          />
        ))}
    </>
  )
}
